import AccountCircleIcon from '@mui/icons-material/AccountCircle'
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined'
import { Avatar, Box, Button, IconButton, Menu, type Theme, Tooltip, useMediaQuery } from '@mui/material'
import { type MouseEvent, type ReactNode, useCallback, useMemo, useState } from 'react'
import { useGetIdentity, UserMenuContextProvider, useTranslate } from 'react-admin'

const avatarSx = { width: 24, height: 24 }

const UserMenu = ({ children }: { children: ReactNode }) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const translate = useTranslate()
  const { isPending, identity } = useGetIdentity()
  const isLargeEnough = useMediaQuery<Theme>((theme) => theme.breakpoints.up('sm'))

  const openMenu = (event: MouseEvent<HTMLElement>) => setAnchorEl(event.currentTarget)
  const closeMenu = useCallback(() => setAnchorEl(null), [])
  const context = useMemo(() => ({ onClose: closeMenu }), [closeMenu])
  const isOpen = Boolean(anchorEl)
  const label = translate('ra.auth.user_menu', { _: 'Profile' })

  const StateIcon = isOpen ? AccountCircleIcon : AccountCircleOutlinedIcon
  const icon =
    !isPending && identity?.avatar ? (
      <Avatar sx={avatarSx} src={identity.avatar} alt={identity.fullName} />
    ) : (
      <StateIcon />
    )

  return (
    <Box>
      {isLargeEnough && !isPending && identity?.fullName ? (
        <Button
          aria-label={label}
          color="inherit"
          startIcon={icon}
          onClick={openMenu}
          variant="text"
          sx={{ textTransform: 'none', marginInlineStart: 0.5 }}
        >
          {identity.fullName}
        </Button>
      ) : (
        <Tooltip title={label}>
          <IconButton
            aria-label={label}
            aria-owns={isOpen ? 'menu-appbar' : undefined}
            aria-haspopup
            color="inherit"
            onClick={openMenu}
          >
            {icon}
          </IconButton>
        </Tooltip>
      )}
      <UserMenuContextProvider value={context}>
        <Menu
          id="menu-appbar"
          disableScrollLock
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          open={isOpen}
          onClose={closeMenu}
        >
          {children}
        </Menu>
      </UserMenuContextProvider>
    </Box>
  )
}

export default UserMenu
